import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts-pro";
import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { OmniSummaryRecord_T } from "../../../types/Brand";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import {
  GridColDef,
  GridInitialState,
  GridSortDirection,
} from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
type Granularity_T = {
  label: string;
  value: string;
};

type ChartDataRecord = {
  x: string;
  y: number | null;
};

const granularityOptions: Granularity_T[] = [
  { label: "Weekly", value: "Week" },
  { label: "Monthly", value: "Month" },
  { label: "Quarterly", value: "Quarter" },
  { label: "Yearly", value: "Year" },
];

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "id", sort: "desc" as GridSortDirection }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const CompletedOmnis: React.FC<any> = () => {
  const [granularity, setGranularity] = useState<Granularity_T>(
    granularityOptions[0]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [omniSummaryRecords, setOmniSummaryRecords] = useState<
    OmniSummaryRecord_T[]
  >([]);
  const [chartData, setChartData] = useState<ChartDataRecord[]>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  // Fetch completed omnis
  const fetchCompletedOmnis = async () => {
    try {
      setLoading(true);
      const response = await apiRequest<OmniSummaryRecord_T[]>(
        `/brand_reports/completed_omnis/${granularity.label}`,
        { method: "GET" }
      );
      if (response.data) setOmniSummaryRecords(response.data);
    } catch (error) {
      console.error(error);
      showError(`Error fetching completed omni stats.`);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const prepareChartData = (records: OmniSummaryRecord_T[]) => {
    return records.map((item) => ({
      x: item.omni_date_period || "",
      y: item.count_omnis ?? null,
    }));
  };

  useEffect(() => {
    fetchCompletedOmnis();
  }, [granularity]);

  useEffect(() => {
    setChartData(prepareChartData(omniSummaryRecords));
  }, [omniSummaryRecords]);

  const renderChart = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (!chartData || chartData.length === 0) {
      return <Typography>No data available for this selection</Typography>;
    }

    return (
      <BarChart
        dataset={chartData}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "x",
            tickPlacement: "middle",
          },
        ]}
        series={[{ dataKey: "y" }]}
        borderRadius={9}
        height={500}
      />
    );
  };

  const columns_def: GridColDef[] = [
    {
      field: "omni_date_period",
      headerName: granularity.label,
      type: "string",
      width: 150,
    },
    {
      field: "count_omnis",
      headerName: "Total Omnis",
      type: "number",
      width: 150,
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <FormControl>
            <InputLabel id="select-granularity-label">Granularity</InputLabel>
            <Select
              labelId="select-granularity-label"
              label="Granularity"
              value={granularity.label}
              onChange={(e) => {
                const selectedOption = granularityOptions.find(
                  (option) => option.label === e.target.value
                );
                if (selectedOption) {
                  setGranularity(selectedOption);
                }
              }}
              style={{ width: "200px" }}
            >
              {granularityOptions.map((option) => (
                <MenuItem key={option.value} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Card className="p-0" style={{ padding: "20px" }}>
          {renderChart()}
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <GaneshDataGrid
              loading={loading}
              data={omniSummaryRecords}
              columns={columns_def}
              gridId="completedOmnisGrid"
              defaultGridState={defaultGridState}
              customHeight="550px"
              showGridSaver={false}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CompletedOmnis;
